import {
  useEffect,
  useMemo,
  useState,
} from "react"

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TablePagination,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"

import { DataGrid } from "@mui/x-data-grid"

import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded"
import PointOfSaleRoundedIcon from "@mui/icons-material/PointOfSaleRounded"
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded"
import DifferenceRoundedIcon from "@mui/icons-material/DifferenceRounded"

import MainLayout from "../../layouts/MainLayout"

import {
  createStoreReconciliation,
  deleteStoreReconciliation,
  getStoreReconciliationById,
  getStoreReconciliationFormData,
  getStoreReconciliations,
  getStoreReconciliationLinks,
  getStoreReconciliationSummary,
  updateStoreReconciliation,
  updateStoreReconciliationLinks,
} from "../../services/storeReconciliationService"


// =========================================================
// CONSTANTS
// =========================================================

const ROWS_PER_PAGE = 10


const defaultFormData = {
  company_id: 1,
  store_id: "",
  period_start: "",
  period_end: "",
  frequency_type: "every_3_days",
  total_sales: 0,
  total_expenses: 0,
  total_deposits: 0,
  expected_amount: 0,
  variance_amount: 0,
  remaining_to_deposit: 0,
  status: "submitted",
  notes: "",
  created_by: 1,
  validated_by: "",
}


// =========================================================
// HELPERS
// =========================================================

function toNumber(value) {
  const number = Number(value || 0)

  return Number.isFinite(number)
    ? number
    : 0
}


function formatAmount(value) {
  return toNumber(value).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}


function formatDate(value) {
  if (!value) return "-"

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) {
    return "-"
  }

  return date.toLocaleDateString("fr-FR")
}


function formatDateTimeForInput(value) {
  if (!value) return ""

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) {
    return ""
  }

  const offset = date.getTimezoneOffset()
  const localDate = new Date(
    date.getTime() - offset * 60000
  )

  return localDate.toISOString().slice(0, 16)
}


function getStatusFallbackLabel(value) {
  const labels = {
    draft: "Brouillon",
    submitted: "Soumis",
    validated: "Validé",
    closed: "Clôturé",
    cancelled: "Annulé",
  }

  return labels[value] || value || "-"
}


function getStatusColor(value) {
  if (value === "validated") return "success"
  if (value === "submitted") return "warning"
  if (value === "closed") return "primary"
  if (value === "draft") return "info"
  if (value === "cancelled") return "error"

  return "default"
}


function getRowClass(status) {
  if (status === "validated") {
    return "reconciliation-row reconciliation-row--validated"
  }

  if (status === "submitted") {
    return "reconciliation-row reconciliation-row--submitted"
  }

  if (status === "closed") {
    return "reconciliation-row reconciliation-row--closed"
  }

  if (status === "cancelled") {
    return "reconciliation-row reconciliation-row--cancelled"
  }

  return "reconciliation-row reconciliation-row--default"
}


function getVarianceColor(value) {
  const number = toNumber(value)

  if (number === 0) {
    return "#15803d"
  }

  if (number > 0) {
    return "#d97706"
  }

  return "#dc2626"
}


function getErrorMessage(error, fallback) {
  const detail = error?.response?.data?.detail

  if (typeof detail === "string") {
    return detail
  }

  if (Array.isArray(detail)) {
    return detail
      .map((item) => item?.msg || JSON.stringify(item))
      .join(" | ")
  }

  if (detail && typeof detail === "object") {
    return detail.message || JSON.stringify(detail)
  }

  return error?.message || fallback
}


// =========================================================
// KPI
// =========================================================

function MetricCard({
  label,
  value,
  caption,
  icon,
  accent = "#2563eb",
  background = "rgba(37, 99, 235, 0.09)",
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        px: { xs: 1.35, sm: 1.5 },
        py: 1.15,
        minWidth: 0,
        borderRadius: 3,
        borderColor: "rgba(148, 163, 184, 0.22)",
        bgcolor: "#ffffff",
        boxShadow: "0 6px 18px rgba(15, 23, 42, 0.035)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.1,
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,
            flexShrink: 0,
            display: "grid",
            placeItems: "center",
            borderRadius: 2.5,
            color: accent,
            bgcolor: background,
          }}
        >
          {icon}
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              gap: 0.7,
              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: 19, sm: 21 },
                lineHeight: 1,
                fontWeight: 900,
                color: "#0f172a",
                whiteSpace: "nowrap",
              }}
            >
              {value}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                minWidth: 0,
                fontWeight: 800,
                color: "#334155",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </Typography>
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              mt: 0.35,
              lineHeight: 1.25,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {caption}
          </Typography>
        </Box>
      </Box>
    </Paper>
  )
}


// =========================================================
// MOBILE CARD
// =========================================================

function ReconciliationMobileCard({
  row,
  statusLabel,
  frequencyLabel,
  onEdit,
  onLinks,
  onDelete,
}) {
  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        borderColor: "rgba(148, 163, 184, 0.22)",
        boxShadow:
          row.status === "validated"
            ? "inset 4px 0 0 #22c55e, 0 5px 16px rgba(15, 23, 42, 0.025)"
            : row.status === "submitted"
            ? "inset 4px 0 0 #f59e0b, 0 5px 16px rgba(15, 23, 42, 0.025)"
            : row.status === "closed"
            ? "inset 4px 0 0 #2563eb, 0 5px 16px rgba(15, 23, 42, 0.025)"
            : "inset 4px 0 0 #cbd5e1, 0 5px 16px rgba(15, 23, 42, 0.025)",
      }}
    >
      <CardContent
        sx={{
          p: 1.5,
          "&:last-child": { pb: 1.5 },
        }}
      >
        <Stack spacing={1.2}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 1,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography fontWeight={900} color="#0f172a">
                {row.reconciliation_code || `RAPP-${row.id}`}
              </Typography>

              <Typography variant="caption" color="text.secondary">
                {frequencyLabel(row.frequency_type)}
              </Typography>
            </Box>

            <Chip
              size="small"
              color={getStatusColor(row.status)}
              label={statusLabel(row.status)}
            />
          </Box>

          <Paper
            variant="outlined"
            sx={{
              p: 1,
              borderRadius: 2.5,
              bgcolor: "#fbfdff",
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
            >
              Shop / période
            </Typography>

            <Typography variant="body2" fontWeight={800} color="#334155">
              {row.store_name || "-"}
            </Typography>

            <Typography variant="caption" color="text.secondary">
              {formatDate(row.period_start)} → {formatDate(row.period_end)}
            </Typography>
          </Paper>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 0.8,
            }}
          >
            <Paper
              variant="outlined"
              sx={{
                p: 0.9,
                borderRadius: 2.5,
                bgcolor: "#f8fafc",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Ventes
              </Typography>

              <Typography fontWeight={900}>
                {formatAmount(row.total_sales)}
              </Typography>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                p: 0.9,
                borderRadius: 2.5,
                bgcolor: "#f8fafc",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Dépôts
              </Typography>

              <Typography fontWeight={900}>
                {formatAmount(row.total_deposits)}
              </Typography>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                p: 0.9,
                borderRadius: 2.5,
                bgcolor: "#f8fafc",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Écart
              </Typography>

              <Typography
                fontWeight={900}
                sx={{
                  color: getVarianceColor(row.variance_amount),
                }}
              >
                {formatAmount(row.variance_amount)}
              </Typography>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                p: 0.9,
                borderRadius: 2.5,
                bgcolor:
                  toNumber(row.remaining_to_deposit) > 0
                    ? "rgba(220, 38, 38, 0.05)"
                    : "rgba(22, 163, 74, 0.05)",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Reste à déposer
              </Typography>

              <Typography
                fontWeight={900}
                color={
                  toNumber(row.remaining_to_deposit) > 0
                    ? "error.main"
                    : "success.main"
                }
              >
                {formatAmount(row.remaining_to_deposit)}
              </Typography>
            </Paper>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(3, minmax(0, 1fr))",
              },
              gap: 0.7,
            }}
          >
            <Button
              variant="contained"
              size="small"
              disableElevation
              onClick={() => onEdit(row.id)}
            >
              Modifier
            </Button>

            <Button
              variant="outlined"
              size="small"
              onClick={() => onLinks(row)}
            >
              Liaisons
            </Button>

            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => onDelete(row.id)}
            >
              Supprimer
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )
}


// =========================================================
// PAGE
// =========================================================

function StoreReconciliationsPage() {
  const theme = useTheme()

  const isMdDown = useMediaQuery(
    theme.breakpoints.down("md")
  )

  const isSmDown = useMediaQuery(
    theme.breakpoints.down("sm")
  )

  const [reconciliations, setReconciliations] = useState([])
  const [stores, setStores] = useState([])
  const [users, setUsers] = useState([])
  const [statuses, setStatuses] = useState([])
  const [frequencyTypes, setFrequencyTypes] = useState([])

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [loadingSummary, setLoadingSummary] = useState(false)

  const [open, setOpen] = useState(false)
  const [selectedReconciliation, setSelectedReconciliation] = useState(null)
  const [formData, setFormData] = useState({
    ...defaultFormData,
  })

  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState("")

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: ROWS_PER_PAGE,
  })

  const [openLinks, setOpenLinks] = useState(false)
  const [selectedLinksReconciliation, setSelectedLinksReconciliation] =
    useState(null)
  const [linksLoading, setLinksLoading] = useState(false)
  const [linksSaving, setLinksSaving] = useState(false)

  const [availableSales, setAvailableSales] = useState([])
  const [availableDeposits, setAvailableDeposits] = useState([])
  const [availableExpenses, setAvailableExpenses] = useState([])

  const [selectedSales, setSelectedSales] = useState([])
  const [selectedDeposits, setSelectedDeposits] = useState([])
  const [selectedExpenses, setSelectedExpenses] = useState([])

  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  })


  const showSnackbar = (
    message,
    severity = "success"
  ) => {
    setSnackbar({
      open: true,
      severity,
      message: String(message || ""),
    })
  }


  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }))
  }


  const statusLabel = (value) => {
    return (
      statuses.find((item) => item.value === value)?.label ||
      getStatusFallbackLabel(value)
    )
  }


  const frequencyLabel = (value) => {
    return (
      frequencyTypes.find((item) => item.value === value)?.label ||
      value ||
      "-"
    )
  }


  const loadReconciliations = async () => {
    try {
      setLoading(true)

      const data = await getStoreReconciliations()

      setReconciliations(
        Array.isArray(data)
          ? data
          : []
      )
    } catch (error) {
      console.error(error)

      showSnackbar(
        getErrorMessage(
          error,
          "Impossible de charger les rapprochements de shop."
        ),
        "error"
      )
    } finally {
      setLoading(false)
    }
  }


  const loadFormData = async () => {
    try {
      const data = await getStoreReconciliationFormData()

      setStores(
        Array.isArray(data?.stores)
          ? data.stores
          : []
      )

      setUsers(
        Array.isArray(data?.users)
          ? data.users
          : []
      )

      setStatuses(
        Array.isArray(data?.statuses)
          ? data.statuses
          : []
      )

      setFrequencyTypes(
        Array.isArray(data?.frequency_types)
          ? data.frequency_types
          : []
      )
    } catch (error) {
      console.error(error)

      showSnackbar(
        getErrorMessage(
          error,
          "Impossible de charger les données du formulaire."
        ),
        "error"
      )
    }
  }


  useEffect(() => {
    loadReconciliations()
    loadFormData()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  const resetForm = () => {
    setSelectedReconciliation(null)

    setFormData({
      ...defaultFormData,
    })
  }


  const openAddDialog = () => {
    resetForm()
    setOpen(true)
  }


  const openEditDialog = async (id) => {
    try {
      const item = await getStoreReconciliationById(id)

      setSelectedReconciliation(item)

      setFormData({
        company_id: item.company_id ?? 1,
        store_id: item.store_id ?? "",

        period_start: formatDateTimeForInput(
          item.period_start
        ),

        period_end: formatDateTimeForInput(
          item.period_end
        ),

        frequency_type:
          item.frequency_type || "every_3_days",

        total_sales:
          Number(item.total_sales || 0),

        total_expenses:
          Number(item.total_expenses || 0),

        total_deposits:
          Number(item.total_deposits || 0),

        expected_amount:
          Number(item.expected_amount || 0),

        variance_amount:
          Number(item.variance_amount || 0),

        remaining_to_deposit:
          Number(item.remaining_to_deposit || 0),

        status:
          item.status || "submitted",

        notes:
          item.notes || "",

        created_by:
          item.created_by ?? 1,

        validated_by:
          item.validated_by ?? "",
      })

      setOpen(true)
    } catch (error) {
      console.error(error)

      showSnackbar(
        getErrorMessage(
          error,
          "Impossible de charger le rapprochement."
        ),
        "error"
      )
    }
  }


  const handleClose = () => {
    if (saving) return

    setOpen(false)
    resetForm()
  }


  const refreshSummary = async (nextData) => {
    if (
      !nextData.store_id ||
      !nextData.period_start ||
      !nextData.period_end
    ) {
      return
    }

    try {
      setLoadingSummary(true)

      const summary = await getStoreReconciliationSummary(
        nextData.store_id,
        nextData.period_start,
        nextData.period_end
      )

      setFormData((prev) => ({
        ...prev,

        total_sales:
          Number(summary.total_sales || 0),

        total_expenses:
          Number(summary.total_expenses || 0),

        total_deposits:
          Number(summary.total_deposits || 0),

        expected_amount:
          Number(summary.expected_amount || 0),

        variance_amount:
          Number(summary.variance_amount || 0),

        remaining_to_deposit:
          Number(summary.remaining_to_deposit || 0),
      }))
    } catch (error) {
      console.error(error)

      showSnackbar(
        getErrorMessage(
          error,
          "Impossible de charger le résumé du rapprochement."
        ),
        "error"
      )
    } finally {
      setLoadingSummary(false)
    }
  }


  const handleChange = async (event) => {
    const { name, value } = event.target

    const nextData = {
      ...formData,

      [name]:
        [
          "company_id",
          "store_id",
          "created_by",
          "validated_by",
        ].includes(name)
          ? value === ""
            ? ""
            : Number(value)
          : value,
    }

    setFormData(nextData)

    if (
      [
        "store_id",
        "period_start",
        "period_end",
      ].includes(name)
    ) {
      await refreshSummary(nextData)
    }
  }


  const validateForm = () => {
    if (!formData.store_id) {
      showSnackbar(
        "Le shop est obligatoire.",
        "warning"
      )
      return false
    }

    if (!formData.period_start) {
      showSnackbar(
        "Le début de période est obligatoire.",
        "warning"
      )
      return false
    }

    if (!formData.period_end) {
      showSnackbar(
        "La fin de période est obligatoire.",
        "warning"
      )
      return false
    }

    if (!formData.frequency_type) {
      showSnackbar(
        "La fréquence est obligatoire.",
        "warning"
      )
      return false
    }

    return true
  }


  const handleSave = async () => {
    if (!validateForm()) return

    const payload = {
      company_id: Number(
        formData.company_id || 1
      ),

      store_id: Number(
        formData.store_id
      ),

      period_start:
        formData.period_start,

      period_end:
        formData.period_end,

      frequency_type:
        formData.frequency_type,

      total_sales:
        Number(formData.total_sales || 0),

      total_expenses:
        Number(formData.total_expenses || 0),

      total_deposits:
        Number(formData.total_deposits || 0),

      expected_amount:
        Number(formData.expected_amount || 0),

      variance_amount:
        Number(formData.variance_amount || 0),

      remaining_to_deposit:
        Number(formData.remaining_to_deposit || 0),

      status:
        formData.status || "submitted",

      notes:
        formData.notes?.trim() || null,

      created_by:
        Number(formData.created_by || 1),

      validated_by:
        formData.validated_by === ""
          ? null
          : Number(formData.validated_by),
    }

    try {
      setSaving(true)

      if (selectedReconciliation?.id) {
        await updateStoreReconciliation(
          selectedReconciliation.id,
          payload
        )

        showSnackbar(
          "Rapprochement modifié avec succès."
        )
      } else {
        await createStoreReconciliation(payload)

        showSnackbar(
          "Rapprochement créé avec succès."
        )
      }

      /*
       * Fermeture directe :
       * handleClose() bloque lorsque saving === true.
       */
      setOpen(false)
      resetForm()

      await loadReconciliations()
    } catch (error) {
      console.error(error)

      showSnackbar(
        getErrorMessage(
          error,
          "Impossible d'enregistrer le rapprochement."
        ),
        "error"
      )
    } finally {
      setSaving(false)
    }
  }


  const handleDelete = async (id) => {
    const confirmed = window.confirm(
      "Voulez-vous vraiment supprimer ce rapprochement ?"
    )

    if (!confirmed) return

    try {
      await deleteStoreReconciliation(id)

      showSnackbar(
        "Rapprochement supprimé avec succès."
      )

      await loadReconciliations()
    } catch (error) {
      console.error(error)

      showSnackbar(
        getErrorMessage(
          error,
          "Impossible de supprimer le rapprochement."
        ),
        "error"
      )
    }
  }


  const openLinksDialog = async (row) => {
    try {
      setLinksLoading(true)
      setSelectedLinksReconciliation(row)

      const data = await getStoreReconciliationLinks(row.id)

      setAvailableSales(
        Array.isArray(data?.sales)
          ? data.sales
          : []
      )

      setAvailableDeposits(
        Array.isArray(data?.deposits)
          ? data.deposits
          : []
      )

      setAvailableExpenses(
        Array.isArray(data?.expenses)
          ? data.expenses
          : []
      )

      setSelectedSales(
        Array.isArray(data?.selected_sales)
          ? data.selected_sales
          : []
      )

      setSelectedDeposits(
        Array.isArray(data?.selected_deposits)
          ? data.selected_deposits
          : []
      )

      setSelectedExpenses(
        Array.isArray(data?.selected_expenses)
          ? data.selected_expenses
          : []
      )

      setOpenLinks(true)
    } catch (error) {
      console.error(error)

      showSnackbar(
        getErrorMessage(
          error,
          "Impossible de charger les liaisons du rapprochement."
        ),
        "error"
      )
    } finally {
      setLinksLoading(false)
    }
  }


  const closeLinksDialog = () => {
    if (linksSaving) return

    setOpenLinks(false)
    setSelectedLinksReconciliation(null)
    setAvailableSales([])
    setAvailableDeposits([])
    setAvailableExpenses([])
    setSelectedSales([])
    setSelectedDeposits([])
    setSelectedExpenses([])
  }


  const toggleSelection = (
    id,
    selectedList,
    setter
  ) => {
    if (selectedList.includes(id)) {
      setter(
        selectedList.filter(
          (item) => item !== id
        )
      )
    } else {
      setter([
        ...selectedList,
        id,
      ])
    }
  }


  const handleSaveLinks = async () => {
    if (!selectedLinksReconciliation?.id) {
      return
    }

    try {
      setLinksSaving(true)

      await updateStoreReconciliationLinks(
        selectedLinksReconciliation.id,
        {
          sales_ids: selectedSales,
          deposit_ids: selectedDeposits,
          expense_ids: selectedExpenses,
        }
      )

      showSnackbar(
        "Liaisons du rapprochement mises à jour avec succès."
      )

      /*
       * Fermeture directe :
       * closeLinksDialog() bloque lorsque linksSaving === true.
       */
      setOpenLinks(false)
      setSelectedLinksReconciliation(null)
      setAvailableSales([])
      setAvailableDeposits([])
      setAvailableExpenses([])
      setSelectedSales([])
      setSelectedDeposits([])
      setSelectedExpenses([])

      await loadReconciliations()
    } catch (error) {
      console.error(error)

      showSnackbar(
        getErrorMessage(
          error,
          "Impossible d'enregistrer les liaisons."
        ),
        "error"
      )
    } finally {
      setLinksSaving(false)
    }
  }


  const filteredReconciliations = useMemo(() => {
    const keyword = search
      .trim()
      .toLowerCase()

    return reconciliations.filter((item) => {
      const matchesSearch =
        keyword === "" ||
        [
          item.reconciliation_code,
          item.store_name,
          item.frequency_type,
          frequencyLabel(item.frequency_type),
          item.status,
          statusLabel(item.status),
        ].some((value) =>
          String(value || "")
            .toLowerCase()
            .includes(keyword)
        )

      const matchesStatus =
        statusFilter === "" ||
        String(item.status || "") === statusFilter

      return (
        matchesSearch &&
        matchesStatus
      )
    })
  }, [
    reconciliations,
    search,
    statusFilter,
    frequencyTypes,
    statuses,
  ])


  const summary = useMemo(() => {
    return filteredReconciliations.reduce(
      (accumulator, item) => {
        accumulator.count += 1
        accumulator.sales += toNumber(item.total_sales)
        accumulator.deposits += toNumber(item.total_deposits)
        accumulator.remaining += toNumber(item.remaining_to_deposit)

        return accumulator
      },
      {
        count: 0,
        sales: 0,
        deposits: 0,
        remaining: 0,
      }
    )
  }, [filteredReconciliations])


  useEffect(() => {
    setPaginationModel((previous) => ({
      ...previous,
      page: 0,
      pageSize: ROWS_PER_PAGE,
    }))
  }, [search, statusFilter])


  const paginatedMobileRows = useMemo(() => {
    const start =
      paginationModel.page * ROWS_PER_PAGE

    return filteredReconciliations.slice(
      start,
      start + ROWS_PER_PAGE
    )
  }, [
    filteredReconciliations,
    paginationModel.page,
  ])


  useEffect(() => {
    const totalPages = Math.max(
      1,
      Math.ceil(
        filteredReconciliations.length / ROWS_PER_PAGE
      )
    )

    if (paginationModel.page >= totalPages) {
      setPaginationModel((previous) => ({
        ...previous,
        page: totalPages - 1,
        pageSize: ROWS_PER_PAGE,
      }))
    }
  }, [
    filteredReconciliations.length,
    paginationModel.page,
  ])


  const resetFilters = () => {
    setSearch("")
    setStatusFilter("")

    setPaginationModel({
      page: 0,
      pageSize: ROWS_PER_PAGE,
    })
  }


  const columns = useMemo(
    () => [
      {
        field: "reconciliation",
        headerName: "Rapprochement",
        minWidth: 190,
        flex: 1,
        sortable: false,
        renderCell: (params) => (
          <Stack
            spacing={0.16}
            justifyContent="center"
            sx={{
              width: "100%",
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <Typography
              sx={{
                color: "#0f172a",
                fontSize: "0.86rem",
                fontWeight: 900,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {params.row.reconciliation_code || `RAPP-${params.row.id}`}
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
            >
              {frequencyLabel(params.row.frequency_type)}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "store_period",
        headerName: "Shop / période",
        minWidth: 220,
        flex: 1.2,
        sortable: false,
        renderCell: (params) => (
          <Stack
            spacing={0.14}
            justifyContent="center"
            sx={{
              width: "100%",
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <Typography
              sx={{
                color: "#334155",
                fontSize: "0.83rem",
                fontWeight: 900,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {params.row.store_name || "-"}
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
            >
              {formatDate(params.row.period_start)} →{" "}
              {formatDate(params.row.period_end)}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "financials",
        headerName: "Activité financière",
        minWidth: 230,
        flex: 1.25,
        sortable: false,
        renderCell: (params) => (
          <Stack spacing={0.12}>
            <Typography
              sx={{
                color: "#0f172a",
                fontSize: "0.82rem",
                fontWeight: 900,
              }}
            >
              Ventes : {formatAmount(params.row.total_sales)}
            </Typography>

            <Typography
              variant="caption"
              sx={{
                color: "#64748b",
                fontWeight: 700,
              }}
            >
              Dépenses : {formatAmount(params.row.total_expenses)}
            </Typography>

            <Typography
              variant="caption"
              sx={{
                color: "#15803d",
                fontWeight: 800,
              }}
            >
              Dépôts : {formatAmount(params.row.total_deposits)}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "balance",
        headerName: "Écart / reste",
        minWidth: 175,
        flex: 0.95,
        sortable: false,
        renderCell: (params) => (
          <Stack spacing={0.12}>
            <Typography
              sx={{
                color: getVarianceColor(params.row.variance_amount),
                fontSize: "0.82rem",
                fontWeight: 900,
              }}
            >
              Écart : {formatAmount(params.row.variance_amount)}
            </Typography>

            <Typography
              variant="caption"
              sx={{
                color:
                  toNumber(params.row.remaining_to_deposit) > 0
                    ? "#dc2626"
                    : "#15803d",
                fontWeight: 900,
              }}
            >
              Reste : {formatAmount(params.row.remaining_to_deposit)}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "status",
        headerName: "Statut",
        width: 125,
        renderCell: (params) => (
          <Chip
            size="small"
            color={getStatusColor(params.value)}
            label={statusLabel(params.value)}
          />
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 270,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.55}>
            <Button
              variant="contained"
              size="small"
              disableElevation
              onClick={() =>
                openEditDialog(params.row.id)
              }
            >
              Modifier
            </Button>

            <Button
              variant="outlined"
              size="small"
              onClick={() =>
                openLinksDialog(params.row)
              }
            >
              Liaisons
            </Button>

            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() =>
                handleDelete(params.row.id)
              }
            >
              Supprimer
            </Button>
          </Stack>
        ),
      },
    ],
    [
      frequencyTypes,
      statuses,
    ]
  )


  return (
    <MainLayout>
      <Box
        sx={{
          width: "100%",
          minWidth: 0,
          pb: { xs: 2, md: 4 },

          "& .MuiButton-root": {
            minHeight: 40,
            borderRadius: 2.5,
            textTransform: "none",
            fontWeight: 800,
            boxShadow: "none",
          },

          "& .MuiOutlinedInput-root": {
            borderRadius: 2.5,
            bgcolor: "#ffffff",
          },

          "& .MuiChip-root": {
            fontWeight: 800,
          },

          "& .MuiPaper-root": {
            backgroundImage: "none",
          },
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "minmax(0, 1fr) auto",
            },
            gap: 1.5,
            alignItems: "start",
            mb: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 900,
                color: "#0f172a",
                lineHeight: 1.12,
                letterSpacing: "-0.025em",
                fontSize: {
                  xs: "1.65rem",
                  sm: "1.95rem",
                  md: "2.1rem",
                },
              }}
            >
              Rapprochements magasins
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.4,
                lineHeight: 1.5,
                maxWidth: 820,
              }}
            >
              Contrôle périodique des ventes, dépenses, dépôts,
              écarts et montants restant à verser.
            </Typography>
          </Box>

          <Button
            variant="contained"
            disableElevation
            onClick={openAddDialog}
            sx={{
              minWidth: { xs: "100%", sm: 210 },
              justifySelf: { xs: "stretch", sm: "end" },
            }}
          >
            + Nouveau rapprochement
          </Button>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              md: "repeat(4, minmax(0, 1fr))",
            },
            gap: 1.1,
            mb: 1.6,
          }}
        >
          <MetricCard
            label="Rapprochements"
            value={summary.count}
            caption="Lignes affichées"
            accent="#2563eb"
            background="rgba(37, 99, 235, 0.09)"
            icon={<FactCheckRoundedIcon fontSize="small" />}
          />

          <MetricCard
            label="Ventes"
            value={formatAmount(summary.sales)}
            caption="Ventes rapprochées"
            accent="#7c3aed"
            background="rgba(124, 58, 237, 0.09)"
            icon={<PointOfSaleRoundedIcon fontSize="small" />}
          />

          <MetricCard
            label="Dépôts"
            value={formatAmount(summary.deposits)}
            caption="Versements rapprochés"
            accent="#16a34a"
            background="rgba(22, 163, 74, 0.09)"
            icon={<AccountBalanceWalletRoundedIcon fontSize="small" />}
          />

          <MetricCard
            label="Reste à déposer"
            value={formatAmount(summary.remaining)}
            caption="Solde encore à verser"
            accent="#dc2626"
            background="rgba(220, 38, 38, 0.09)"
            icon={<DifferenceRoundedIcon fontSize="small" />}
          />
        </Box>

        <Paper
          variant="outlined"
          sx={{
            p: { xs: 1.15, sm: 1.25 },
            mb: 1.6,
            borderRadius: 3,
            borderColor: "rgba(148, 163, 184, 0.22)",
            boxShadow: "0 5px 16px rgba(15, 23, 42, 0.025)",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                lg: "minmax(0, 1fr) 220px 135px",
              },
              gap: 1,
              alignItems: "center",
            }}
          >
            <TextField
              fullWidth
              size="small"
              label="Recherche"
              placeholder="Code, shop, fréquence, statut..."
              value={search}
              onChange={(event) =>
                setSearch(event.target.value)
              }
            />

            <TextField
              select
              fullWidth
              size="small"
              label="Statut"
              value={statusFilter}
              onChange={(event) =>
                setStatusFilter(event.target.value)
              }
            >
              <MenuItem value="">Tous</MenuItem>

              {statuses.map((status) => (
                <MenuItem
                  key={status.value}
                  value={status.value}
                >
                  {status.label}
                </MenuItem>
              ))}
            </TextField>

            <Button
              variant="outlined"
              onClick={resetFilters}
              sx={{
                width: "100%",
                minWidth: 0,
                whiteSpace: "nowrap",
              }}
            >
              Réinitialiser
            </Button>
          </Box>
        </Paper>

        <Paper
          variant="outlined"
          sx={{
            borderRadius: 3.5,
            overflow: "hidden",
            borderColor: "rgba(148, 163, 184, 0.22)",
            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.035)",
          }}
        >
          <Box
            sx={{
              px: { xs: 1.4, sm: 1.8 },
              py: 1.15,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              gap: 0.8,
              borderBottom: "1px solid",
              borderColor: "divider",
              bgcolor: "#ffffff",
            }}
          >
            <Box>
              <Typography fontWeight={900} color="#0f172a">
                Registre des rapprochements
              </Typography>

              <Typography variant="caption" color="text.secondary">
                Shop, période, activité financière, écart et statut
              </Typography>
            </Box>

            <Chip
              size="small"
              variant="outlined"
              label={`${filteredReconciliations.length} rapprochement${
                filteredReconciliations.length > 1 ? "s" : ""
              }`}
            />
          </Box>

          {!isMdDown ? (
            <Box
              sx={{
                width: "100%",
                minWidth: 0,
                overflowX: "auto",
                bgcolor: "#ffffff",
              }}
            >
              <Box sx={{ minWidth: 1120 }}>
                <DataGrid
                  autoHeight
                  rows={filteredReconciliations}
                  columns={columns}
                  loading={loading}
                  disableRowSelectionOnClick
                  disableColumnSelector
                  pagination
                  paginationMode="client"
                  paginationModel={paginationModel}
                  onPaginationModelChange={(model) =>
                    setPaginationModel({
                      page: model.page,
                      pageSize: ROWS_PER_PAGE,
                    })
                  }
                  pageSizeOptions={[ROWS_PER_PAGE]}
                  rowHeight={76}
                  columnHeaderHeight={50}
                  getRowClassName={(params) =>
                    getRowClass(params.row.status)
                  }
                  localeText={{
                    noRowsLabel: "Aucun rapprochement",
                    noResultsOverlayLabel: "Aucun résultat",
                    MuiTablePagination: {
                      labelRowsPerPage: "",
                      labelDisplayedRows: ({
                        from,
                        to,
                        count,
                      }) => `${from}–${to} sur ${count}`,
                    },
                  }}
                  sx={{
                    border: 0,
                    width: "100%",
                    color: "#334155",
                    fontSize: "0.83rem",
                    bgcolor: "#ffffff",

                    "& .MuiDataGrid-columnHeaders": {
                      bgcolor: "#f8fafc",
                      borderBottom: "1px solid #dfe7f1",
                    },

                    "& .MuiDataGrid-columnHeader": {
                      bgcolor: "#f8fafc",
                      px: 1.25,
                      borderRight:
                        "1px solid rgba(226, 232, 240, 0.60)",
                    },

                    "& .MuiDataGrid-columnHeaderTitle": {
                      color: "#334155",
                      fontSize: "0.76rem",
                      fontWeight: 900,
                      letterSpacing: "0.025em",
                    },

                    "& .MuiDataGrid-cell": {
                      px: 1.25,
                      minWidth: 0,
                      display: "flex",
                      alignItems: "center",
                      overflow: "hidden",
                      borderBottom: "1px solid #edf2f7",
                      borderRight:
                        "1px solid rgba(241, 245, 249, 0.85)",
                    },

                    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within":
                      {
                        outline: "none",
                      },

                    "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
                      {
                        outline: "none",
                      },

                    "& .reconciliation-row": {
                      transition:
                        "background-color 0.16s ease, box-shadow 0.16s ease",
                    },

                    "& .reconciliation-row--validated": {
                      boxShadow: "inset 4px 0 0 #22c55e",
                      bgcolor: "rgba(240, 253, 244, 0.30)",
                    },

                    "& .reconciliation-row--submitted": {
                      boxShadow: "inset 4px 0 0 #f59e0b",
                      bgcolor: "rgba(255, 251, 235, 0.34)",
                    },

                    "& .reconciliation-row--closed": {
                      boxShadow: "inset 4px 0 0 #2563eb",
                      bgcolor: "rgba(239, 246, 255, 0.34)",
                    },

                    "& .reconciliation-row--cancelled": {
                      boxShadow: "inset 4px 0 0 #ef4444",
                      bgcolor: "rgba(254, 242, 242, 0.30)",
                    },

                    "& .reconciliation-row--default": {
                      boxShadow: "inset 4px 0 0 #cbd5e1",
                    },

                    "& .reconciliation-row:hover": {
                      bgcolor: "#f8fbff !important",
                      boxShadow:
                        "inset 4px 0 0 #2563eb, 0 4px 14px rgba(15, 23, 42, 0.045)",
                    },

                    "& .MuiDataGrid-footerContainer": {
                      minHeight: 52,
                      bgcolor: "#fbfdff",
                      borderTop: "1px solid #dfe7f1",
                    },

                    "& .MuiTablePagination-selectLabel": {
                      display: "none",
                    },

                    "& .MuiTablePagination-input": {
                      display: "none",
                    },
                  }}
                />
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                p: { xs: 1.1, sm: 1.25 },
                bgcolor: "#f8fafc",
              }}
            >
              {loading ? (
                <Box sx={{ py: 5, textAlign: "center" }}>
                  <Typography color="text.secondary">
                    Chargement des rapprochements...
                  </Typography>
                </Box>
              ) : paginatedMobileRows.length === 0 ? (
                <Box sx={{ py: 5, textAlign: "center" }}>
                  <Typography color="text.secondary">
                    Aucun rapprochement trouvé.
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1}>
                  {paginatedMobileRows.map((row) => (
                    <ReconciliationMobileCard
                      key={row.id}
                      row={row}
                      statusLabel={statusLabel}
                      frequencyLabel={frequencyLabel}
                      onEdit={openEditDialog}
                      onLinks={openLinksDialog}
                      onDelete={handleDelete}
                    />
                  ))}
                </Stack>
              )}

              <TablePagination
                component="div"
                count={filteredReconciliations.length}
                page={paginationModel.page}
                onPageChange={(_event, newPage) =>
                  setPaginationModel((previous) => ({
                    ...previous,
                    page: newPage,
                    pageSize: ROWS_PER_PAGE,
                  }))
                }
                rowsPerPage={ROWS_PER_PAGE}
                rowsPerPageOptions={[ROWS_PER_PAGE]}
                labelRowsPerPage=""
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}–${to} sur ${count}`
                }
                sx={{
                  mt: 1,
                  bgcolor: "#ffffff",
                  borderRadius: 2.5,

                  "& .MuiTablePagination-selectLabel": {
                    display: "none",
                  },

                  "& .MuiTablePagination-input": {
                    display: "none",
                  },
                }}
              />
            </Box>
          )}
        </Paper>
      </Box>

      {/* =================================================
          MAIN DIALOG
      ================================================= */}

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        fullScreen={isSmDown}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 4 },
            m: { xs: 0, sm: 2 },
            maxHeight: { xs: "100dvh", sm: "92dvh" },
          },
        }}
      >
        <DialogTitle sx={{ pb: 1.3 }}>
          <Typography fontWeight={900} fontSize={20} color="#0f172a">
            {selectedReconciliation
              ? "Modifier le rapprochement"
              : "Nouveau rapprochement"}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.3 }}
          >
            Sélectionnez un shop et une période. Les montants sont calculés
            automatiquement à partir des opérations de la période.
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 3,
                borderColor: "rgba(148, 163, 184, 0.22)",
              }}
            >
              <Typography
                fontWeight={900}
                color="#0f172a"
                sx={{ mb: 1.2 }}
              >
                Période et shop
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(2, minmax(0, 1fr))",
                  },
                  gap: 1.5,
                }}
              >
                <TextField
                  select
                  size="small"
                  label="Shop"
                  name="store_id"
                  value={formData.store_id}
                  onChange={handleChange}
                  fullWidth
                  required
                  disabled={saving}
                >
                  {stores.map((store) => (
                    <MenuItem key={store.id} value={store.id}>
                      {store.code} - {store.name}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  size="small"
                  label="Fréquence"
                  name="frequency_type"
                  value={formData.frequency_type}
                  onChange={handleChange}
                  fullWidth
                  required
                  disabled={saving}
                >
                  {frequencyTypes.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  type="datetime-local"
                  size="small"
                  label="Début de période"
                  name="period_start"
                  value={formData.period_start}
                  onChange={handleChange}
                  fullWidth
                  required
                  disabled={saving}
                  InputLabelProps={{ shrink: true }}
                />

                <TextField
                  type="datetime-local"
                  size="small"
                  label="Fin de période"
                  name="period_end"
                  value={formData.period_end}
                  onChange={handleChange}
                  fullWidth
                  required
                  disabled={saving}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            </Paper>

            {loadingSummary && (
              <Alert severity="info" variant="outlined">
                Calcul du résumé du shop...
              </Alert>
            )}

            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 3,
                bgcolor: "#f8fafc",
                borderColor: "rgba(148, 163, 184, 0.22)",
              }}
            >
              <Typography
                fontWeight={900}
                color="#0f172a"
                sx={{ mb: 1.2 }}
              >
                Résumé financier calculé
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                    md: "repeat(3, minmax(0, 1fr))",
                  },
                  gap: 1.2,
                }}
              >
                <TextField
                  size="small"
                  label="Total ventes"
                  value={formatAmount(formData.total_sales)}
                  fullWidth
                  disabled
                />

                <TextField
                  size="small"
                  label="Total dépenses"
                  value={formatAmount(formData.total_expenses)}
                  fullWidth
                  disabled
                />

                <TextField
                  size="small"
                  label="Total dépôts"
                  value={formatAmount(formData.total_deposits)}
                  fullWidth
                  disabled
                />

                <TextField
                  size="small"
                  label="Montant attendu"
                  value={formatAmount(formData.expected_amount)}
                  fullWidth
                  disabled
                />

                <TextField
                  size="small"
                  label="Écart"
                  value={formatAmount(formData.variance_amount)}
                  fullWidth
                  disabled
                  sx={{
                    "& .MuiInputBase-input": {
                      fontWeight: 900,
                      color: getVarianceColor(formData.variance_amount),
                    },
                  }}
                />

                <TextField
                  size="small"
                  label="Reste à déposer"
                  value={formatAmount(formData.remaining_to_deposit)}
                  fullWidth
                  disabled
                  sx={{
                    "& .MuiInputBase-input": {
                      fontWeight: 900,
                      color:
                        toNumber(formData.remaining_to_deposit) > 0
                          ? "#dc2626"
                          : "#15803d",
                    },
                  }}
                />
              </Box>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 3,
                borderColor: "rgba(148, 163, 184, 0.22)",
              }}
            >
              <Typography
                fontWeight={900}
                color="#0f172a"
                sx={{ mb: 1.2 }}
              >
                Validation
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(2, minmax(0, 1fr))",
                  },
                  gap: 1.5,
                }}
              >
                <TextField
                  select
                  size="small"
                  label="Statut"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  fullWidth
                  disabled={saving}
                >
                  {statuses.map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  size="small"
                  label="Validé par"
                  name="validated_by"
                  value={formData.validated_by}
                  onChange={handleChange}
                  fullWidth
                  disabled={saving}
                >
                  <MenuItem value="">Aucun</MenuItem>

                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Paper>

            <TextField
              label="Notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              fullWidth
              multiline
              minRows={3}
              disabled={saving}
            />
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            gap: 1,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Button
            onClick={handleClose}
            disabled={saving}
          >
            Annuler
          </Button>

          <Button
            variant="contained"
            disableElevation
            onClick={handleSave}
            disabled={saving}
          >
            {saving
              ? "Enregistrement..."
              : selectedReconciliation
              ? "Modifier"
              : "Enregistrer"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* =================================================
          LINKS DIALOG
      ================================================= */}

      <Dialog
        open={openLinks}
        onClose={closeLinksDialog}
        fullWidth
        maxWidth="md"
        fullScreen={isSmDown}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 4 },
            m: { xs: 0, sm: 2 },
            maxHeight: { xs: "100dvh", sm: "92dvh" },
          },
        }}
      >
        <DialogTitle sx={{ pb: 1.3 }}>
          <Typography fontWeight={900} fontSize={20} color="#0f172a">
            Liaisons du rapprochement
            {selectedLinksReconciliation?.reconciliation_code
              ? ` — ${selectedLinksReconciliation.reconciliation_code}`
              : ""}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.3 }}
          >
            Sélectionnez les ventes, dépôts et dépenses qui doivent être
            rattachés à ce rapprochement.
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          {linksLoading ? (
            <Alert severity="info" variant="outlined">
              Chargement des opérations liées...
            </Alert>
          ) : (
            <Stack spacing={2.2}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.4,
                  borderRadius: 3,
                  borderColor: "rgba(148, 163, 184, 0.22)",
                }}
              >
                <Typography fontWeight={900} color="#0f172a" mb={1}>
                  Ventes
                </Typography>

                <Stack spacing={0.4}>
                  {availableSales.length === 0 ? (
                    <Typography color="text.secondary" variant="body2">
                      Aucune vente dans cette période.
                    </Typography>
                  ) : (
                    availableSales.map((item) => (
                      <FormControlLabel
                        key={item.id}
                        control={
                          <Checkbox
                            checked={selectedSales.includes(item.id)}
                            onChange={() =>
                              toggleSelection(
                                item.id,
                                selectedSales,
                                setSelectedSales
                              )
                            }
                          />
                        }
                        label={`${item.code} | ${formatAmount(
                          item.total_amount
                        )} | ${item.customer_name || "Client comptant"}`}
                      />
                    ))
                  )}
                </Stack>
              </Paper>

              <Divider />

              <Paper
                variant="outlined"
                sx={{
                  p: 1.4,
                  borderRadius: 3,
                  bgcolor: "#f8fafc",
                  borderColor: "rgba(148, 163, 184, 0.22)",
                }}
              >
                <Typography fontWeight={900} color="#0f172a" mb={1}>
                  Dépôts
                </Typography>

                <Stack spacing={0.4}>
                  {availableDeposits.length === 0 ? (
                    <Typography color="text.secondary" variant="body2">
                      Aucun dépôt dans cette période.
                    </Typography>
                  ) : (
                    availableDeposits.map((item) => (
                      <FormControlLabel
                        key={item.id}
                        control={
                          <Checkbox
                            checked={selectedDeposits.includes(item.id)}
                            onChange={() =>
                              toggleSelection(
                                item.id,
                                selectedDeposits,
                                setSelectedDeposits
                              )
                            }
                          />
                        }
                        label={`${item.code} | ${formatAmount(
                          item.amount
                        )} | ${item.deposit_method || "-"}`}
                      />
                    ))
                  )}
                </Stack>
              </Paper>

              <Divider />

              <Paper
                variant="outlined"
                sx={{
                  p: 1.4,
                  borderRadius: 3,
                  borderColor: "rgba(148, 163, 184, 0.22)",
                }}
              >
                <Typography fontWeight={900} color="#0f172a" mb={1}>
                  Dépenses
                </Typography>

                <Stack spacing={0.4}>
                  {availableExpenses.length === 0 ? (
                    <Typography color="text.secondary" variant="body2">
                      Aucune dépense dans cette période.
                    </Typography>
                  ) : (
                    availableExpenses.map((item) => (
                      <FormControlLabel
                        key={item.id}
                        control={
                          <Checkbox
                            checked={selectedExpenses.includes(item.id)}
                            onChange={() =>
                              toggleSelection(
                                item.id,
                                selectedExpenses,
                                setSelectedExpenses
                              )
                            }
                          />
                        }
                        label={`${item.code} | ${formatAmount(
                          item.amount
                        )} | ${item.expense_type || "-"}`}
                      />
                    ))
                  )}
                </Stack>
              </Paper>
            </Stack>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            gap: 1,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Button
            onClick={closeLinksDialog}
            disabled={linksSaving}
          >
            Annuler
          </Button>

          <Button
            variant="contained"
            disableElevation
            onClick={handleSaveLinks}
            disabled={linksSaving}
          >
            {linksSaving
              ? "Enregistrement..."
              : "Enregistrer les liaisons"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={handleCloseSnackbar}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: "100%",
            borderRadius: 3,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainLayout>
  )
}

export default StoreReconciliationsPage
